import React, { useState, useRef, useEffect, useCallback } from "react";
import { v4 as uuidv4 } from "uuid";
import { createPortal } from "react-dom";

import useScroll from "../../../hooks/useScroll";

import styles from "./Select.module.scss";
import { convertToBoolean } from "../../../_utils";

import Checkbox from "../Checkbox";

import { MdArrowDropDown } from "react-icons/md";
import { FaTimes } from "react-icons/fa";
import { translate } from "../../../hooks/translate";

/** Only one Select dropdown open at a time (portals are outside the DOM tree). */
let activeSelectCloser = null;

const SelectedList = ({ items, unique, isWrapped, onRemove }) => {
  const maxVisible = unique ? 1 : 3;
  const safeItems = Array.isArray(items) ? items : [];
  const visibleItems = safeItems.slice(0, maxVisible);
  const hiddenCount = Math.max(0, safeItems.length - visibleItems.length);
  return (
    <div
      className={`${styles["selected-list"]} ${
        unique && styles["selected-list--unique"]
      }`}
      style={{ padding: isWrapped ? "0 5px" : "5px 10px 5px 5px" }}
    >
      {visibleItems.map((current, index) => (
        <div key={index} className={styles["selected-list__selection"]}>
          <div
            className={`d-flex align-items-center ${styles["selected-list__selection--ellipsis"]}`}
            title={current?.label || ""}
          >
            {current?.label || ""}

            {!unique && (
              <FaTimes
                className={styles["selected-list__selection--remove"]}
                onClick={(event) => onRemove?.(event, current)}
              />
            )}
          </div>
        </div>
      ))}
      {hiddenCount > 0 && (
        <div className={styles["selected-list__selection"]}>
          <div className="d-flex align-items-center">+{hiddenCount}</div>
        </div>
      )}
    </div>
  );
};

const OptionItem = ({ option, unique, onClick }) => (
  <li onClick={onClick} className={styles["option-item"]}>
    <div
      className={`${styles["option-item__item"]} ${
        option?.checked ? styles["option-item__item--checked"] : ""
      }`}
    >
      {!unique && <Checkbox isChecked={Boolean(option?.checked)} size="small" />}
      <span className={styles["option-item__name"]}>{option?.label || ""}</span>
    </div>
  </li>
);

const Select = ({
  label,
  options,
  selected,
  onSelected,
  value,
  onChange,
  name,
  unique,
  placeholder,
  valid,
  message,
  required,
  disabled = false,
  className,
  searchInDropdown = false,
  searchPlaceholder = "Ara...",
  onDropdownSearchChange,
}) => {
  const [selectionOptions, setSelectionOptions] = useState([]);
  const [currentSelection, setCurrentSelection] = useState([]);
  const [onSelect, setOnSelect] = useState(false);
  const [dropdownSearchQuery, setDropdownSearchQuery] = useState("");
  const [state, setState] = useState({ valid: undefined });

  const selectRef = useRef(null);
  const searchRef = useRef(null);
  const dropdownSearchRef = useRef(null);
  const dropdownWrapperRef = useRef(null);

  const toggleDropdownId = useRef(`toggle-dropdown-${uuidv4()}`);

  const { top } = useScroll();

  const closeDropdown = useCallback(() => {
    setOnSelect(false);
    setDropdownSearchQuery("");
    onDropdownSearchChange?.("");
    if (activeSelectCloser === closeDropdown) {
      activeSelectCloser = null;
    }
  }, [onDropdownSearchChange]);

  useEffect(
    () => () => {
      if (activeSelectCloser === closeDropdown) {
        activeSelectCloser = null;
      }
    },
    [closeDropdown],
  );

  const filterOptions = (list, query) => {
    if (!query?.trim()) return list;
    const q = query.toLowerCase();
    return list?.filter((option) =>
      option?.label?.toLowerCase().includes(q),
    );
  };

  useEffect(() => {
    const rawSelected =
      selected !== undefined
        ? selected
        : value !== undefined && value !== null && value !== ""
          ? Array.isArray(value)
            ? value
            : [value]
          : [];

    const safeOptions = Array.isArray(options) ? options.filter(Boolean) : [];

    const isOptionSelected = (optVal) => {
      const optStr = String(optVal ?? "");
      return Array.isArray(rawSelected) && rawSelected.some((r) => {
        const rVal = r !== null && typeof r === "object" && r.value !== undefined ? r.value : r;
        const rStr = String(rVal ?? "");
        return rStr === optStr;
      });
    };

    const values = safeOptions.map((option) => ({
      ...option,
      ...(isOptionSelected(option?.value) && { checked: true }),
    }));

    setCurrentSelection(
      values.filter((option) => isOptionSelected(option?.value)) || [],
    );

    const query = searchInDropdown ? dropdownSearchQuery : "";
    setSelectionOptions(filterOptions(values, query));
    setState({ valid: convertToBoolean(valid) });
  }, [options, selected, value, valid, dropdownSearchQuery, searchInDropdown]);

  useEffect(() => {
    if (!onSelect) return;

    const handleClickOutside = (event) => {
      if (
        selectRef.current?.contains(event.target) ||
        dropdownWrapperRef.current?.contains(event.target)
      ) {
        return;
      }

      closeDropdown();
    };

    document.addEventListener("click", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("click", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [onSelect, closeDropdown]);

  useEffect(() => {
    if (onSelect) calculatePosition();
  }, [top, onSelect]);

  const handleToggleDropdown = (event) => {
    event.stopPropagation();
    if (disabled) return;

    const newState = !onSelect;

    if (newState) {
      if (activeSelectCloser && activeSelectCloser !== closeDropdown) {
        activeSelectCloser();
      }
      activeSelectCloser = closeDropdown;
      setOnSelect(true);
      if (searchInDropdown) {
        setDropdownSearchQuery("");
        onDropdownSearchChange?.("");
      }
      setTimeout(() => {
        calculatePosition();
        const focusRef = searchInDropdown ? dropdownSearchRef : searchRef;
        focusRef?.current?.focus();
      }, 10);
    } else {
      closeDropdown();
    }

    setState({ valid: undefined });
  };

  const calculatePosition = () => {
    if (
      !selectRef?.current ||
      !dropdownWrapperRef?.current ||
      !dropdownWrapperRef.current.style
    ) {
      return;
    }

    const rect = selectRef.current.getBoundingClientRect();
    const spaceAbove = rect.top;
    const spaceBelow = window.innerHeight - rect.bottom;

    dropdownWrapperRef.current.style.left = `${rect.left + window.scrollX}px`;
    dropdownWrapperRef.current.style.width = `${rect.width}px`;

    if (spaceBelow < 250 && spaceAbove > 250) {
      dropdownWrapperRef.current.style.bottom = `${
        window.innerHeight - rect.top + window.scrollY
      }px`;
      dropdownWrapperRef.current.style.top = "auto";
    } else {
      dropdownWrapperRef.current.style.top = `${
        rect.bottom + window.scrollY
      }px`;
      dropdownWrapperRef.current.style.bottom = "auto";
    }
  };

  const searchForOptions = (value) => {
    if (searchInDropdown) return;

    if (!value) {
      setSelectionOptions([...options]);
      return;
    }

    setSelectionOptions(filterOptions(options, value));
  };

  const optionSelected = (option) => {
    if (!option) return;
    if (unique) {
      setCurrentSelection([option]);
      onSelected?.([option]);
      onChange?.({ target: { value: option?.value, name } });
      closeDropdown();
      return;
    }

    const exists = currentSelection?.find(
      (current) => current?.value === option?.value,
    );

    let values;

    if (exists) {
      values = currentSelection.filter(
        (current) => current?.value !== option?.value,
      );
    } else {
      values = [...currentSelection, option];
    }

    setCurrentSelection(values);
    onSelected?.(values);
    onChange?.({ target: { value: values.map((v) => v?.value), name } });
  };

  const remove = (event, select) => {
    event.stopPropagation();

    const values = currentSelection?.filter(
      (current) => current?.value !== select?.value,
    );

    setCurrentSelection(values);
    onSelected?.(values);
    onChange?.({
      target: { value: unique ? "" : values?.map((v) => v?.value), name },
    });
  };

  return (
    <div
      className={`m-select ${styles["select-container"]} ${className || ""}`}
      ref={selectRef}
      style={{ position: "relative" }}
    >
      {label ? (
        <label className={styles["select-container__label"]}>
          <span>{label}</span>
          {required && (
            <span className={styles["select-container__label--required"]}>
              *
            </span>
          )}
        </label>
      ) : null}

      <div
        className={`m-select-selected ${styles["select-container__selected"]} ${
          onSelect ? styles["select-container__selected--active"] : ""
        } ${disabled ? styles["select-container__selected--disabled"] : ""}`}
        onClick={handleToggleDropdown}
        id={toggleDropdownId.current}
      >
        <input
          type="text"
          className={styles["select-container__selected--search"]}
          ref={searchRef}
          readOnly={searchInDropdown}
          onChange={(e) => searchForOptions(e.target.value)}
          disabled={disabled}
          placeholder={currentSelection.length ? "" : placeholder || ""}
        />

        <SelectedList
          items={currentSelection}
          unique={unique}
          onRemove={(event, current) => remove(event, current)}
        />

        <MdArrowDropDown
          className={`${styles["icon-select"]} ${
            onSelect ? styles["icon-select--expanded"] : ""
          }`}
        />
      </div>

      {onSelect &&
        createPortal(
          <div
            className={styles["select-container__dropdown-wrapper"]}
            ref={dropdownWrapperRef}
            style={{ position: "fixed", zIndex: 9999 }}
          >
            {searchInDropdown && (
              <div className={styles["select-container__dropdown-search"]}>
                <input
                  type="text"
                  ref={dropdownSearchRef}
                  value={dropdownSearchQuery}
                  onChange={(e) => {
                    const v = e.target.value;
                    setDropdownSearchQuery(v);
                    onDropdownSearchChange?.(v);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  placeholder={searchPlaceholder}
                />
              </div>
            )}
            <ul
              className={`${styles["select-container__options"]} ${
                searchInDropdown
                  ? styles["select-container__options--in-dropdown"]
                  : ""
              }`}
            >
              {selectionOptions?.map((option, index) => (
                <OptionItem
                  key={index}
                  option={option}
                  unique={unique}
                  onClick={() => optionSelected(option)}
                />
              ))}

              {!selectionOptions?.length && (
                <span>{translate("UI.NO_DATA")}</span>
              )}
            </ul>
          </div>,
          document.body,
        )}

      {state?.valid !== undefined && (
        <span
          className={`${styles["select-container__message"]} ${
            state?.valid
              ? styles["select-container__message--valid"]
              : styles["select-container__message--invalid"]
          }`}
        >
          {message || (state?.valid ? "Success" : "Error Text")}
        </span>
      )}
    </div>
  );
};

export default Select;
